import { useEffect, useState } from 'react';

import { fromAnalyView, getDetail } from '../server';

import type { DiagnosisRecord } from './toDiagnosisRecord';

/**
 * useSharedResult — 공유 링크(`/share/:id`)로 열린 회차 하나를 서버에서 읽는다.
 *
 * 수신 화면의 조회·상태를 여기 한 곳에 둔다. 없는 id(404)·삭제된 회차·미완료 회차·API 꺼짐·
 * 호출 실패는 전부 `unavailable` 한 갈래다 — 보는 사람에게 원인을 나눠 보여 줄 이유가 없다.
 *
 * `fromAnalyView` 의 설문값(`answers`)은 빈 값이다: 수신자에게는 보낸 사람의 로컬 설문이 없고,
 * 이 화면이 그리는 점수·보이스 타입은 설문값에 기대지 않는다(`toSurvey` 가 담는 것은
 * 성별·출생년도뿐이고 화면에 쓰이지 않는다).
 * 서버 id 는 경로 조립에만 쓴다(`getDetail`).
 */
export type SharedResult =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; record: DiagnosisRecord };

const LOADING: SharedResult = { status: 'loading' };

/**
 * 세션이 서기를 기다리는 상한. 링크로 처음 들어온 기기는 등록·토큰 갱신부터 해야 해서 다른
 * 호출의 2초로는 모자란다 — 로컬 dev 서버에서 갱신에 4.8초가 걸려 요청 없이 "볼 수 없어요" 가
 * 떴다(2026-10-08 관측 1회, 운영 빌드는 미측정). 이 화면은 이 조회 말고 그릴 것이 없으니 기다린다.
 */
const SESSION_WAIT_MS = 10_000;

export const useSharedResult = (id: string): SharedResult => {
  // 어느 id 의 결과인지 함께 든다 — id 가 바뀐 첫 렌더에 이전 회차가 비치지 않게.
  const [settled, setSettled] = useState<{
    id: string;
    result: SharedResult;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getDetail(id, { sessionWaitMs: SESSION_WAIT_MS }).then((view) => {
      if (cancelled) {
        return;
      }
      const record = view ? fromAnalyView(view, {}) : null;
      setSettled({
        id,
        result: record
          ? { status: 'ready', record }
          : { status: 'unavailable' },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return settled?.id === id ? settled.result : LOADING;
};
